import { useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import { useQuery } from '@apollo/client';
import _ from 'lodash';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { GetCollectionDocument } from 'src/gql/graphql';

/**
 * Any collection (custom category or not) is normally only reachable at
 * /collections/:slugOrId, even collections that are configured to appear in the
 * storefront nav as a "custom category" and are presented to shoppers as just
 * another category.
 *
 * This hook lets the /products/:category route "just work" for any collection:
 * if the `:category` param doesn't match a real product category, we check
 * whether it matches a collection (by slug or id) and, if so, return it so the
 * page can render the collection instead of falling back to an unfiltered
 * product list.
 *
 * Resolution happens in two steps:
 * 1. Fast path: check dispensary.collectionsNavigation (already loaded on the
 *    dispensary object, scoped server-side to customCategory: true, enabled: true)
 *    for a synchronous, no-extra-request match.
 * 2. Fallback: query GetCollection directly, which resolves ANY collection
 *    (customCategory or not) by slug or id, same as the /collections/:slug page.
 *
 * @param {object|null} matchedCategory - result of useDispensaryCategory(); when
 *   truthy, the route already resolved to a real category and no fallback lookup
 *   is needed.
 * @returns {{ id: string, slug: string }|null} the matching collection, or null
 */
export function useCollectionFallbackForProductsRoute(matchedCategory) {
  const router = useRouter();
  const { dispensary } = useDispensary();
  const categoryParam = router?.query?.category;

  const navMatch = useMemo(() => {
    if (matchedCategory || !categoryParam) {
      return null;
    }

    const collections = dispensary?.collectionsNavigation;
    if (!collections) {
      return null;
    }

    const normalizedParam = _.toLower(categoryParam);

    return (
      _.find(
        collections,
        (collection) => _.toLower(collection?.slug) === normalizedParam || collection?.id === categoryParam
      ) ?? null
    );
  }, [matchedCategory, categoryParam, dispensary?.collectionsNavigation]);

  const { data } = useQuery(GetCollectionDocument, {
    skip: !!matchedCategory || !categoryParam || !dispensary?.id || !!navMatch,
    variables: {
      collectionId: categoryParam,
      dispensaryId: dispensary?.id,
    },
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
  });

  return useMemo(() => {
    if (navMatch) {
      return navMatch;
    }

    const collection = data?.getCollection;
    if (collection?.id && collection?.enabled !== false) {
      return collection;
    }

    return null;
  }, [navMatch, data]);
}
